<template>
  <!-- The map's pin-to-screen: the app icon, and in Safari the steps to add it, since there is no prompt to call. -->
  <div v-if="canInstall" class="GamInstallPin">
    <div v-if="showIOSInstructions" class="install-backdrop" @click="dismissIOSInstructions" />
    <Transition name="install-popup">
      <div v-if="showIOSInstructions" class="install-popup" @click="dismissIOSInstructions">
        <span class="install-title">{{ localize('pwa.install.ios.title') }}</span>
        <span>
          {{ localize(isMacSafari ? 'pwa.install.mac.instruction.prefix' : 'pwa.install.ios.instruction.prefix') }}
          <svg class="install-step-icon" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="50" height="50" rx="7" fill="currentColor" fill-opacity="0.08" />
            <path
              d="M25 10l-7 7m7-7l7 7m-7-7v22M15 22v14a2 2 0 002 2h16a2 2 0 002-2V22"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ localize(isMacSafari ? 'pwa.install.mac.instruction.suffix' : 'pwa.install.ios.instruction.suffix') }}
          <svg
            v-if="isMacSafari"
            class="install-step-icon"
            viewBox="0 0 50 50"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="50" height="50" rx="7" fill="currentColor" fill-opacity="0.08" />
            <rect x="11" y="14" width="28" height="22" rx="4" stroke="currentColor" stroke-width="2" />
            <path d="M16 30h18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
          </svg>
          <svg v-else class="install-step-icon" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="50" height="50" rx="7" fill="currentColor" fill-opacity="0.08" />
            <rect x="12" y="12" width="26" height="26" rx="5" stroke="currentColor" stroke-width="2" />
            <path d="M25 19v12M19 25h12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </span>
      </div>
    </Transition>
    <button type="button" class="install-icon" :aria-label="localize('pwa.install.ios.title')" @click="install">
      <img src="/favicon/app-icon-v6.png" alt="" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import { isMacSafari } from '@/core/helpers/ui.helper';
import { useInstallPrompt } from '@/views/composables/hooks/useInstallPrompt';

const { canInstall, showIOSInstructions, install, dismissIOSInstructions } = useInstallPrompt();
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.GamInstallPin {
  position: relative;
  display: flex;
}

.install-icon {
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @include press;
}

.install-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1;
}

/* Above the icon, as the map's opens above its own, with its right edge on the icon's so it opens into the screen. */
.install-popup {
  position: absolute;
  bottom: calc(100% + var(--spacing-md));
  right: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 220px;
  padding: var(--spacing-l);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-l);
  background: var(--color-dark-600);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
  color: var(--color-white-70);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  text-align: left;
  cursor: pointer;
}

.install-title {
  font-weight: 600;
  color: var(--color-white);
}

.install-step-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  margin: 0 2px;
  vertical-align: middle;
  color: var(--color-primary);
}

.install-popup-enter-active,
.install-popup-leave-active {
  transition: opacity var(--transition);
}

.install-popup-enter-from,
.install-popup-leave-to {
  opacity: 0;
}
</style>
